/** Shopify Admin URLs for orders and products. */

export function shopifyStoreHandle(storeDomain) {
  const raw = String(storeDomain || '').trim().toLowerCase();
  if (!raw) return '';
  return raw.replace('.myshopify.com', '').split('.')[0];
}

export function shopifyOrderAdminUrl(storeDomain, orderId) {
  const handle = shopifyStoreHandle(storeDomain);
  const id = String(orderId || '').trim();
  if (!handle || !id) return null;
  return `https://admin.shopify.com/store/${handle}/orders/${id}`;
}

/** Admin orders index pre-filtered by tag — e.g. all "Pick exception" holds. */
export function shopifyOrdersByTagAdminUrl(storeDomain, tag) {
  const handle = shopifyStoreHandle(storeDomain);
  const tagValue = String(tag || '').trim();
  if (!handle || !tagValue) return null;
  return `https://admin.shopify.com/store/${handle}/orders?tag=${encodeURIComponent(tagValue)}`;
}

/** Admin orders index showing these orders (free-text A OR B search).
 * Deliberately NOT `name:X` — the new admin orders index accepts that chip
 * but applies no filter (verified live 2026-08-25: name:FPV127485 listed all
 * 494 orders, plain FPV127485 returned exactly the one). Free text with OR
 * returns every named order, occasionally plus a fuzzy extra on multi-name
 * searches — prefer shopifyOrderAdminUrl(id) when an order id is at hand.
 *
 * Multi-name searches add a `created_at:>=` floor so the fuzzy extras (years-old
 * orders such as CCC3156 for CCC23020 OR …) drop out. Unlike `name:`, the admin
 * index DOES apply created_at (verified live 2026-09-30: the CCC23020/23011/22993
 * search went from 5 rows incl. two 2021 orders to exactly the 3). `since` is the
 * earliest order date (`slackDays`, default one, is taken off for timezones);
 * without one the floor is DEFAULT_SINCE_DAYS back. Pass a wider `slackDays`
 * when `since` is only a record date that can trail the order (preorders). */
const DEFAULT_SINCE_DAYS = 365;
const DAY_MS = 24 * 60 * 60 * 1000;

function createdAtFloor(since, now = Date.now(), slackDays = 1) {
  const list = (Array.isArray(since) ? since : [since])
    .map((value) => (value ? new Date(value).getTime() : NaN))
    .filter(Number.isFinite);
  const earliest = list.length ? Math.min(...list) - slackDays * DAY_MS : now - DEFAULT_SINCE_DAYS * DAY_MS;
  return new Date(earliest).toISOString().slice(0, 10);
}

export function shopifyOrdersByNamesAdminUrl(storeDomain, orderNames, { since, now, slackDays } = {}) {
  const handle = shopifyStoreHandle(storeDomain);
  const names = [...new Set((orderNames || [])
    .map((name) => String(name || '').trim().replace(/^#/, ''))
    .filter(Boolean))];
  if (!handle || !names.length) return null;
  // A single plain name matches exactly; only OR searches pick up fuzzy extras.
  const query = names.length === 1
    ? names[0]
    : `(${names.join(' OR ')}) created_at:>=${createdAtFloor(since, now, slackDays)}`;
  return `https://admin.shopify.com/store/${handle}/orders?query=${encodeURIComponent(query)}`;
}

/** Shopify does not document a search length cap; long admin URLs start to
 * fail around 2k characters, so "Open all" splits well before that. */
export const SHOPIFY_OPEN_ALL_MAX_NAMES = 40;
export const SHOPIFY_OPEN_ALL_MAX_URL_LENGTH = 1800;

/** "Open all in Shopify" links for many orders of ONE store: as few links as
 * fit the limits above, each the same A OR B search as
 * shopifyOrdersByNamesAdminUrl, floored at that chunk's oldest order. A chunk
 * of one order with a numeric Shopify id opens that order directly.
 * `orders`: [{ orderNumber, createdAt?, shopifyOrderId? }]. Returns
 * [{ url, count, first, last }] where first/last are 1-based positions. */
export function shopifyOrdersOpenAllLinks(storeDomain, orders, {
  maxNames = SHOPIFY_OPEN_ALL_MAX_NAMES,
  maxUrlLength = SHOPIFY_OPEN_ALL_MAX_URL_LENGTH,
  now
} = {}) {
  const seen = new Set();
  const list = (orders || []).filter((order) => {
    const name = String(order?.orderNumber || '').trim().replace(/^#/, '');
    if (!name || seen.has(name.toUpperCase())) return false;
    seen.add(name.toUpperCase());
    return true;
  });
  const chunkUrl = (chunk) => {
    if (chunk.length === 1 && /^[1-9]\d*$/.test(String(chunk[0].shopifyOrderId || ''))) {
      return shopifyOrderAdminUrl(storeDomain, chunk[0].shopifyOrderId);
    }
    return shopifyOrdersByNamesAdminUrl(
      storeDomain,
      chunk.map((order) => order.orderNumber),
      { since: chunk.map((order) => order.createdAt).filter(Boolean), now }
    );
  };

  const links = [];
  let chunk = [];
  let start = 0;
  const flush = () => {
    if (!chunk.length) return;
    const url = chunkUrl(chunk);
    if (url) links.push({ url, count: chunk.length, first: start + 1, last: start + chunk.length });
    start += chunk.length;
    chunk = [];
  };
  list.forEach((order) => {
    const next = [...chunk, order];
    const nextUrl = next.length > 1 ? chunkUrl(next) : '';
    if (chunk.length && (next.length > maxNames || String(nextUrl || '').length > maxUrlLength)) {
      flush();
      chunk = [order];
    } else {
      chunk = next;
    }
  });
  flush();
  return links;
}
